// Server-injected config (wp_localize_script -> window.altFreeTrialConfig).

export interface SubjectTile {
    abbr: string;
    title: string;
    color: string;
}

export interface AltFreeTrialConfig {
    restUrl: string;
    nonce: string;
    logoUrl: string;
    schoolName: string;
    privacyUrl: string;
    termsUrl: string;
    successRedirectUrl: string;
    metaPixelId: string;
    // Program-specific — injected per route (A Level at /free-trial, O Level at
    // /o-level). Falls back to the A Level set below when not provided.
    level: string;
    eyebrow: string;
    contentName: string;
    subjects: string[];
    grades: string[];
    tiles: SubjectTile[];
}

declare global {
    interface Window {
        altFreeTrialConfig?: Partial<AltFreeTrialConfig>;
        fbq?: (...args: unknown[]) => void;
        dataLayer?: Record<string, unknown>[];
    }
}

// ---- A Level defaults (also the fallback when the server omits program data) ----
const A_LEVEL_SUBJECTS = [
    'Physics',
    'Chemistry',
    'Biology',
    'Psychology',
    'Computer Science',
    'Mathematics',
    'Economics',
    'Accounting',
    'Business',
];

const A_LEVEL_GRADES = ['AS', 'A2'];

const A_LEVEL_TILES: SubjectTile[] = [
    { abbr: 'Ch', title: 'Chemistry', color: '#BB1450' },
    { abbr: 'Ph', title: 'Physics', color: '#332B8F' },
    { abbr: 'Bi', title: 'Biology', color: '#1C7A40' },
    { abbr: 'Ma', title: 'Mathematics', color: '#006DA3' },
    { abbr: 'Cs', title: 'Computer Science', color: '#C92A1E' },
    { abbr: 'Bu', title: 'Business', color: '#7A2A5C' },
    { abbr: 'Ec', title: 'Economics', color: '#1D7261' },
    { abbr: 'Ac', title: 'Accounting', color: '#3041D9' },
    { abbr: 'Ps', title: 'Psychology', color: '#633073' },
];

const fallback: AltFreeTrialConfig = {
    restUrl: '/wp-json/',
    nonce: '',
    logoUrl: '',
    schoolName: 'Alt Academy',
    privacyUrl: '#',
    termsUrl: '#',
    successRedirectUrl: '',
    metaPixelId: '',
    level: 'A Level',
    eyebrow: 'Free trial · A Level',
    contentName: 'A Level Free Trial',
    subjects: A_LEVEL_SUBJECTS,
    grades: A_LEVEL_GRADES,
    tiles: A_LEVEL_TILES,
};

const injected = window.altFreeTrialConfig ?? {};

export const config: AltFreeTrialConfig = {
    ...fallback,
    ...injected,
    // Guard against the server injecting empty arrays for these.
    subjects: injected.subjects?.length ? injected.subjects : fallback.subjects,
    grades: injected.grades?.length ? injected.grades : fallback.grades,
    tiles: injected.tiles?.length ? injected.tiles : fallback.tiles,
};

// Back-compat exports — now program-aware (driven by the injected config).
export const GRADES = config.grades;
export const SUBJECTS = config.subjects;

export const HEAR_ABOUT = [
    'Youtube',
    'Google Search',
    'Instagram',
    'Facebook',
    'At an event',
    'Friends & Family',
    'Other',
] as const;
